/**
 * Seller concession dollar/percent conversion and bounds (issue #240).
 *
 * This is a DELIBERATE DUPLICATE of the C# seam in
 * Controllers/ListNLock/ListNLockPrivateHelpers.cs (region "Seller concession —
 * dollar/percent entry"). It is not a call into it: converting on every keystroke
 * cannot be a server round trip, and this repo has no JavaScript test runner
 * (.claude/rules/lock-products-controller-testability.md), so this file ships with
 * zero automated coverage.
 *
 * The C# seam is the REFERENCE IMPLEMENTATION. If the two ever disagree, the C#
 * side is right and this file is the bug — the server re-validates every
 * submission, so a divergence here shows up as a rejected form, never as a bad
 * rate. Member names, ordering and message strings are mirrored one-for-one so
 * the two files can be diffed side by side; that diff is the entire QA story for
 * this code. Keep it that way when editing either side.
 *
 * No jQuery, no DOM — pure functions only, so the arithmetic can be reasoned
 * about without a page.
 */

/** Mirrors SellerConcessionUnit. */
export const SellerConcessionUnit = {
    Percent: 'percent',
    Dollar: 'dollar'
};

/** Mirrors SellerConcessionStatus. */
export const SellerConcessionStatus = {
    Valid: 'Valid',
    Missing: 'Missing',
    NotNumeric: 'NotNumeric',
    BelowMinimum: 'BelowMinimum',
    AboveMaximum: 'AboveMaximum',
    NoConversionBase: 'NoConversionBase'
};

export const MinSellerConcessionPercent = 1;
export const MaxSellerConcessionPercent = 6;

/**
 * Below this sales price a percent -> dollar -> percent round trip can move the
 * percentage outside [1, 6] at 2dp, so dollar entry is switched off rather than
 * allowed to drift. See the C# seam for the derivation and the P = 3010
 * counterexample.
 */
export const MinimumConversionSalesPrice = 10000;

const ConcessionRangeMessage = 'Seller concession should be within 1-6% range.';

// NumberStyles.Number equivalent: optional sign, digits, optional single decimal
// point. Deliberately rejects exponent form ("1e3"), which .NET's NumberStyles.Number
// also rejects — Number() would have accepted it.
const NUMERIC = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
});

/**
 * Mirrors FormatConcessionDollars — en-US "C0", e.g. 18000 -> "$18,000".
 *
 * Only ever called on the non-negative 1%/6% bounds. Do not reuse for arbitrary
 * signed values: .NET's en-US "C0" renders a negative as "($18,000)" while Intl
 * renders "-$18,000", so the mirror does not hold below zero.
 */
export function formatConcessionDollars(wholeDollars) {
    return CURRENCY_FORMAT.format(wholeDollars);
}

/**
 * Half-up rounding, mirroring Math.Round(value, decimals, MidpointRounding.AwayFromZero).
 *
 * The scaling goes through the number's DECIMAL STRING, not a binary multiply.
 * Neither obvious alternative works: 1.005 * 100 is 100.49999999999999 in binary
 * floating point, so `Math.round(v * 100) / 100` yields 1.00; and (1.005).toFixed(2)
 * is also "1.00", because the double nearest 1.005 is slightly below it. Re-parsing
 * "1.005e2" instead produces exactly 100.5, which rounds to 1.01 — matching the C#
 * decimal seam.
 *
 * C# rounds decimals, which are exact; this rounds doubles, which are not. The
 * string route closes the gap for every value this feature can produce (concessions
 * of a few digits, prices under eight), not for doubles in general.
 */
export function roundHalfUp(value, decimals) {
    if (typeof value !== 'number' || !isFinite(value)) {
        return NaN;
    }

    const sign = value < 0 ? -1 : 1;
    const abs = Math.abs(value);
    const text = String(abs);

    // Already in exponential form (< 1e-6 or >= 1e21) — string shifting would build
    // "1e-7e2". Nothing in this feature's range reaches here; fall back rather than
    // return NaN.
    if (text.indexOf('e') !== -1 || text.indexOf('E') !== -1) {
        const factor = Math.pow(10, decimals);
        return sign * (Math.round(abs * factor) / factor);
    }

    const shifted = Number(text + 'e' + decimals);
    // Math.round is half-up for non-negative input, which is why the sign is
    // stripped above: Math.round(-0.5) is -0, i.e. half-toward-positive.
    return sign * Number(Math.round(shifted) + 'e-' + decimals);
}

/** Mirrors HasUsableConversionBase. */
export function hasUsableConversionBase(salesPrice) {
    return typeof salesPrice === 'number'
        && isFinite(salesPrice)
        && salesPrice >= MinimumConversionSalesPrice;
}

/** Mirrors ConvertConcessionToDollars. Whole dollars, half-up. Null with no usable base. */
export function convertConcessionToDollars(percent, salesPrice) {
    if (!hasUsableConversionBase(salesPrice)) {
        return null;
    }

    return roundHalfUp(percent / 100 * salesPrice, 0);
}

/** Mirrors ConvertConcessionToPercent. Half-up to 2dp. Null with no usable base. */
export function convertConcessionToPercent(dollars, salesPrice) {
    if (!hasUsableConversionBase(salesPrice)) {
        return null;
    }

    return roundHalfUp(dollars * 100 / salesPrice, 2);
}

/**
 * Reads a display figure such as "$650,000" or "6.5" into a number.
 * Returns null — NOT 0 — for blank or malformed input, so "nothing entered" stays
 * distinguishable from "entered zero". Number('') is 0, which is exactly the
 * conflation this avoids.
 */
export function parseNumber(text) {
    if (text === null || text === undefined) {
        return null;
    }

    // Strip the currency symbol and grouping separators, mirroring the C# seam's
    // explicit "$" strip plus NumberStyles.Number grouping support.
    const normalized = String(text).replace(/\$/g, '').replace(/,/g, '').trim();

    if (normalized === '' || !NUMERIC.test(normalized)) {
        return null;
    }

    const value = Number(normalized);
    return isFinite(value) ? value : null;
}

function entry(status, fields) {
    const result = {
        status: status,
        isValid: status === SellerConcessionStatus.Valid,
        percent: 0,
        dollars: null,
        minimumDollars: null,
        maximumDollars: null,
        message: null
    };

    return Object.assign(result, fields || {});
}

function accept(percent, salesPrice) {
    return entry(SellerConcessionStatus.Valid, {
        percent: percent,
        // Recomputed FROM the canonical percentage, never echoed from the entry.
        // This is what puts every accepted result on the (percent, dollars) fixed
        // point, so toggling units is idempotent by construction rather than by
        // arithmetic luck.
        dollars: convertConcessionToDollars(percent, salesPrice),
        minimumDollars: convertConcessionToDollars(MinSellerConcessionPercent, salesPrice),
        maximumDollars: convertConcessionToDollars(MaxSellerConcessionPercent, salesPrice)
    });
}

function evaluatePercentConcession(rawPercent, salesPrice) {
    // Round BEFORE checking, so a value is accepted as the figure that will be
    // persisted: 6.004 passes as 6.00, 6.005 rounds to 6.01 and is rejected.
    const percent = roundHalfUp(rawPercent, 2);

    if (percent < MinSellerConcessionPercent) {
        return entry(SellerConcessionStatus.BelowMinimum, { message: ConcessionRangeMessage });
    }

    if (percent > MaxSellerConcessionPercent) {
        return entry(SellerConcessionStatus.AboveMaximum, { message: ConcessionRangeMessage });
    }

    return accept(percent, salesPrice);
}

function evaluateDollarConcession(rawDollars, salesPrice) {
    if (!hasUsableConversionBase(salesPrice)) {
        return entry(SellerConcessionStatus.NoConversionBase);
    }

    // Round the bounds ONCE, then use the same figures for both the comparison and
    // the message — comparing against an unrounded 18000.60 while quoting "$18,001"
    // would produce copy the arithmetic contradicts.
    const dollars = roundHalfUp(rawDollars, 0);
    const minimum = convertConcessionToDollars(MinSellerConcessionPercent, salesPrice);
    const maximum = convertConcessionToDollars(MaxSellerConcessionPercent, salesPrice);

    if (dollars < minimum) {
        return entry(SellerConcessionStatus.BelowMinimum, {
            minimumDollars: minimum,
            maximumDollars: maximum,
            message: 'Seller concession must be at least 1% of the sales price ('
                + formatConcessionDollars(minimum) + ').'
        });
    }

    if (dollars > maximum) {
        return entry(SellerConcessionStatus.AboveMaximum, {
            minimumDollars: minimum,
            maximumDollars: maximum,
            message: 'Seller concession cannot exceed 6% of the sales price ('
                + formatConcessionDollars(maximum) + ').'
        });
    }

    // The decision above was made in whole dollars, deliberately: $18,001 on a
    // $300,000 price fails even though it rounds to 6.00%, because the error copy
    // names $18,000 as the limit and must be literally true.
    return accept(convertConcessionToPercent(dollars, salesPrice), salesPrice);
}

/**
 * Mirrors EvaluateConcessionEntry. Total: never throws, for any input including
 * null, "abc", "1.2.3" and "$18,000".
 */
export function evaluateConcessionEntry(text, unit, salesPrice) {
    const value = parseNumber(text);

    if (value === null) {
        // parseNumber collapses blank and malformed into null; recover the C#
        // distinction so callers can tell "nothing typed yet" from "typed garbage".
        const isBlank = text === null || text === undefined || String(text).trim() === '';
        return entry(isBlank ? SellerConcessionStatus.Missing : SellerConcessionStatus.NotNumeric);
    }

    return unit === SellerConcessionUnit.Dollar
        ? evaluateDollarConcession(value, salesPrice)
        : evaluatePercentConcession(value, salesPrice);
}

/**
 * Mirrors EvaluateConcessionPercent.
 *
 * A null percent is Missing, NOT a revert. The Confirmation page's "Revert to
 * Original Concession" button deliberately posts no value at all; callers must
 * check for that before calling this.
 */
export function evaluateConcessionPercent(percent, salesPrice) {
    if (typeof percent !== 'number' || !isFinite(percent)) {
        return entry(SellerConcessionStatus.Missing);
    }

    return evaluatePercentConcession(percent, salesPrice);
}
